/**
 * AuditList — Activity section on `/users/:userId`.
 *
 * Tier 0 (slice-local). Renders the cursor-paginated audit feed with
 * human-readable event labels and a "Load more" affordance for the
 * next cursor page.
 *
 * Event-label mapping is in-file for Phase 3 (no i18n yet). Future:
 * lift the mapping into react-intl + a typed payload schema per
 * event_type so payloads can be rendered structurally instead of
 * skipped.
 */

import * as React from 'react';
import { Button } from '@sparta/ui-kit';
import { useUserAudit } from '../api/use-user-audit.js';
import type { AuditEventRow } from '../api/types.js';

interface AuditListProps {
  readonly userId: string;
}

/**
 * Known event_type → label mapping. Sourced from
 * `apps/api/src/modules/identity/application/.../*.use-case.ts`
 * (see grep for `eventType: 'user.*'`).
 */
const EVENT_LABELS: Record<string, string> = {
  'user.profile_updated': 'Profile updated',
  'user.session_revoked': 'Session revoked',
  'user.verification_resent': 'Verification email resent',
  'user.password_reset_sent': 'Password reset email sent',
  'user.invited': 'Invitation sent',
  'user.invitation_resent': 'Invitation resent',
  'user.invitation_revoked': 'Invitation revoked',
};

function labelFor(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType;
}

function formatTimestamp(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function abbreviateActor(uuid: string): string {
  if (uuid.length <= 8) return uuid;
  return `${uuid.slice(0, 4)}…${uuid.slice(-4)}`;
}

export function AuditList({ userId }: AuditListProps): React.ReactElement {
  const audit = useUserAudit(userId);

  if (audit.status === 'loading') {
    return (
      <div
        aria-busy="true"
        aria-label="Loading activity"
        className="text-sm text-[var(--fg-tertiary)]"
      >
        Loading activity…
      </div>
    );
  }
  if (audit.status === 'forbidden') {
    return (
      <div role="alert" className="text-sm text-[var(--fg-secondary)]">
        You don’t have permission to view this user’s activity.
      </div>
    );
  }
  if (audit.status === 'notFound') {
    return (
      <div role="alert" className="text-sm text-[var(--fg-secondary)]">
        Activity not found for this user.
      </div>
    );
  }
  if (audit.status === 'error') {
    return (
      <div role="alert" className="flex flex-col gap-3 text-sm text-[var(--fg-secondary)]">
        <p>We couldn’t load the activity feed.</p>
        <div>
          <Button variant="secondary" size="sm" onClick={() => audit.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const events: readonly AuditEventRow[] = audit.events;
  if (events.length === 0) {
    return (
      <p className="text-sm text-[var(--fg-tertiary)]">
        No recent activity for this user.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ul aria-label="Activity feed" className="flex flex-col gap-2">
        {events.map((e) => (
          <li
            key={e.id}
            className="border-b border-[var(--border-subtle)] pb-2 text-sm"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-semibold text-[var(--fg-primary)]">
                {labelFor(e.eventType)}
              </span>
              <span className="text-xs text-[var(--fg-tertiary)]">
                {formatTimestamp(e.createdAt)}
              </span>
            </div>
            <p className="mt-1 text-xs text-[var(--fg-secondary)]">
              by{' '}
              {e.actorEmail !== null ? (
                // Email is the canonical identifier an admin recognises
                // in the audit feed. The BE resolves the actor UUID
                // against the org-member projection; when the lookup
                // succeeds we render the resolved email and keep the
                // raw UUID off-screen.
                <span>{e.actorEmail}</span>
              ) : (
                // Fallback: actor is no longer a member (or audit row
                // carries a null actor for a system action). Show the
                // abbreviated UUID in mono so it reads as an opaque id
                // rather than a username.
                <span className="font-mono">
                  {abbreviateActor(e.actorUserId)}
                </span>
              )}
            </p>
          </li>
        ))}
      </ul>

      {audit.hasNext ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            disabled={audit.isFetchingNext}
            onClick={() => audit.fetchNext()}
          >
            {audit.isFetchingNext ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
